import { z } from "zod";
import { AccountId, SubWalletId, TransactionId, WalletId } from "./ids.ts";
import { MoneyAmount } from "./money.ts";

/** Tx classifier — drives status machine + which checks apply on authorize. */
export const TransactionKind = z.enum([
  "buy", // fiat → coin (closed-loop in)
  "transfer", // wallet → wallet
  "spend", // sub-wallet → merchant/agent payee
  "reversal", // netting entry against an earlier tx
  "adjustment", // ops-side correction (audited)
]);
export type TransactionKind = z.output<typeof TransactionKind>;

/** Lifecycle state for a transaction. Reversal of a posted tx writes a new tx. */
export const TransactionStatus = z.enum([
  "pending", // accepted, not yet posted to the ledger
  "posted", // ledger entries written
  "failed", // never reached posted
  "reversed", // a later reversal tx nets this one (informational on the original)
]);
export type TransactionStatus = z.output<typeof TransactionStatus>;

/**
 * Free-form per-tx context (gateway ref, idempotency key, agent token id, …).
 * Keep JSON-safe: no bigints, no Date — callers stringify before assigning.
 */
export const TransactionMetadata = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean(), z.null()]),
);
export type TransactionMetadata = z.output<typeof TransactionMetadata>;

/**
 * Transaction header. Refines enforce two invariants that catch foot-guns at
 * parse time: every tx has at least one of from/to wallet, and
 * `reversesTransactionId` is set iff `kind === "reversal"`.
 */
export const Transaction = z
  .object({
    id: TransactionId,
    accountId: AccountId,
    kind: TransactionKind,
    status: TransactionStatus,
    amount: MoneyAmount,
    fromWalletId: WalletId.nullable(),
    fromSubWalletId: SubWalletId.nullable(),
    toWalletId: WalletId.nullable(),
    toSubWalletId: SubWalletId.nullable(),
    reversesTransactionId: TransactionId.nullable(),
    metadata: TransactionMetadata,
    createdAt: z.coerce.date(),
    postedAt: z.coerce.date().nullable(),
  })
  .refine((t) => t.fromWalletId !== null || t.toWalletId !== null, {
    message: "transaction must have a fromWalletId or toWalletId",
    path: ["fromWalletId"],
  })
  .refine((t) => (t.kind === "reversal") === (t.reversesTransactionId !== null), {
    message: "reversesTransactionId is required iff kind === 'reversal'",
    path: ["reversesTransactionId"],
  });
export type Transaction = z.output<typeof Transaction>;
